import React from 'react';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Theme } from '@mui/material/styles';

export type ButtonGroupWithBadgeItem = {
  /**
   * Unique identifier for the item.
   */
  id: string;
  /**
   * Text label shown inside the button.
   */
  label: string;
  /**
   * Optional counter value shown as a circular badge to the right of the label.
   * If undefined or null the badge is hidden. If 0 the badge is shown only when
   * `showZeroCounts` is true.
   */
  count?: number | null;
  /**
   * If true the button will be disabled.
   */
  disabled?: boolean;
};

export type ButtonGroupWithBadgeProps = {
  /**
   * Items to render as buttons. Each item should include `id`, `label`, and
   * optional `count` and `disabled`.
   */
  items: ButtonGroupWithBadgeItem[];
  /**
   * The id of the currently selected item (optional).
   */
  selectedId?: string | null;
  /**
   * Callback fired when an item is clicked. Receives the item's id.
   */
  onChange?: (id: string) => void;
  /**
   * Size applied to buttons. One of "small" | "medium" | "large".
   * @default "medium"
   */
  size?: 'small' | 'medium' | 'large';
  /**
   * MUI color applied to the button group (affects outlined border / hover).
   * Typical values: "primary" | "secondary" | "inherit" | "success" | "error" | "info" | "warning".
   * @default "primary"
   */
  color?: 'primary' | 'secondary' | 'inherit' | 'success' | 'error' | 'info' | 'warning';
  /**
   * Theme palette key used to style the active (selected) button background and text.
   * This should be a key from Theme['palette'] such as "primary", "info", "success", etc.
   * @default "primary"
   */
  activeColor?: keyof Theme['palette'];
  /**
   * Theme palette key used for the badge background and text color.
   * @default "secondary"
   */
  badgeColor?: keyof Theme['palette'];
  /**
   * If true the buttons will stretch to fill the group width.
   * @default false
   */
  fullWidth?: boolean;
  /**
   * If true, show zero counts. Otherwise, counts of 0 / null / undefined are hidden.
   * @default false
   */
  showZeroCounts?: boolean;
  /**
   * If true, buttons are rendered individually instead of inside a ButtonGroup.
   * @default false
   */
  ungrouped?: boolean;
};

/**
 * ButtonGroupWithBadge
 *
 * A reusable ButtonGroup component that renders an array of buttons where each
 * button shows:
 *  - a left-aligned label
 *  - a circular counter badge to the right (optional)
 *
 * The selected button receives an "active" background color controlled by `activeColor`.
 *
 * @param {ButtonGroupWithBadgeProps} props
 * @param props.items - Items to render (id, label, optional count, optional disabled)
 * @param props.selectedId - Currently selected item id (optional)
 * @param props.onChange - Callback when a button is clicked; receives the item's id
 * @param props.size - Button size: "small" | "medium" | "large" (default: "medium")
 * @param props.color - MUI color for the ButtonGroup (default: "primary")
 * @param props.activeColor - Theme palette key for selected button background (default: "primary")
 * @param props.badgeColor - Theme palette key for the badge background (default: "secondary")
 * @param props.fullWidth - Whether the ButtonGroup is full width (default: false)
 * @param props.showZeroCounts - If true, show badges for counts === 0 (default: false)
 * @param props.ungrouped - If true, show individual buttons (default: false)
 *
 * @example
 * // Basic usage in a React component (TypeScript)
 * import React, { useState } from "react";
 * import ButtonGroupWithBadge from "./components/ButtonGroupWithBadge";
 *
 * function Example() {
 *   const [selected, setSelected] = useState<string | null>('');
 *
 *   const handleChange = (id: string) => {
 *     console.log('Selected:', id);
 *     setSelected(id === selected ? '' : id);
 *   };
 *
 *   const items = [
 *     { id: "inbox", label: "Inbox", count: 12 },
 *     { id: "starred", label: "Starred", count: 3 },
 *     { id: "drafts", label: "Drafts", count: 0 },
 *     { id: "spam", label: "Spam", count: null, disabled: true },
 *   ];
 *
 *   return (
 *     <ButtonGroupWithBadge
 *       items={items}
 *       selectedId={selected}
 *       onChange={handleChange}
 *       size="medium"
 *       color="primary"
 *       activeColor="info"
 *       badgeColor="secondary"
 *       fullWidth={false}
 *       showZeroCounts={false}
 *       ungrouped={false}
 *     />
 *   );
 * }
 */
const ButtonGroupWithBadge: React.FunctionComponent<ButtonGroupWithBadgeProps> = ({
  items,
  selectedId = null,
  onChange,
  size = 'medium',
  color = 'primary',
  activeColor = 'primary',
  badgeColor = 'secondary',
  fullWidth = false,
  showZeroCounts = false,
  ungrouped,
}) => {
  const renderButton = (item: ButtonGroupWithBadgeItem) => {
    const showBadge = item.count !== undefined && item.count !== null && (showZeroCounts ? true : item.count > 0);
    const isSelected = selectedId !== null && selectedId === item.id;

    return (
      <Button
        key={item.id}
        onClick={() => onChange && onChange(item.id)}
        disabled={item.disabled}
        aria-pressed={isSelected}
        variant={'outlined'}
        // color={ungrouped ? 'secondary' : color}
        sx={(theme: Theme) => {
          // Cast activeColor to a keyof Theme['palette'] so TS knows we're indexing with a valid palette key.
          const activeKey = activeColor as keyof Theme['palette'];
          // theme.palette[...] can produce different shapes (text/background/action), so cast to `any`
          // when accessing `.main` / `.contrastText` / `.dark`. This keeps TS happy while still being safe
          // for typical palette color keys like "primary", "secondary", "info", etc.
          const paletteColor = (theme.palette[activeKey] as any) ?? theme.palette.primary;

          const base = {
            px: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: 1,
            width: fullWidth ? '100%' : 'auto',
            borderColor: `${paletteColor.main}`,
            '&.Mui-disabled': {
              borderColor: `${paletteColor.main}`,
            },
          };

          const selectedStyles =
            isSelected && !item.disabled
              ? {
                  bgcolor: paletteColor.main,
                  color: paletteColor.contrastText,
                  '&:hover': {
                    bgcolor: paletteColor.dark ?? paletteColor.main,
                  },
                }
              : {};

          return {
            ...base,
            ...selectedStyles,
          };
        }}
      >
        <Box
          component={'span'}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            width: '100%',
            justifyContent: 'space-between',
          }}
        >
          <Typography component={'span'} variant={'body2'} sx={{ fontWeight: 'bold', textTransform: 'none' }}>
            {item.label}
          </Typography>

          {showBadge ? (
            <Box
              component={'span'}
              sx={(theme: Theme) => {
                const badgeKey = badgeColor as keyof Theme['palette'];
                const badgePalette = (theme.palette[badgeKey] as any) ?? theme.palette.secondary;
                return {
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 999,
                  height: 20,
                  minWidth: 20,
                  px: '6px',
                  fontSize: 12,
                  lineHeight: '20px',
                  backgroundColor: badgePalette.main,
                  color: badgePalette.contrastText,
                };
              }}
            >
              {item.count}
            </Box>
          ) : null}
        </Box>
      </Button>
    );
  };

  if (ungrouped) {
    return (
      <Box display={'flex'} gap={1} flexWrap={'wrap'} width={fullWidth ? '100%' : 'auto'}>
        {items.map(renderButton)}
      </Box>
    );
  }

  return (
    <ButtonGroup
      variant={'outlined'}
      color={color}
      size={size}
      fullWidth={fullWidth}
      aria-label={'button group with counters'}
    >
      {items.map(renderButton)}
    </ButtonGroup>
  );
};

export default ButtonGroupWithBadge;
